import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";

const PageContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 30px 20px;
`;

const Header = styled.header`
  margin-bottom: 30px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 28px;
  color: #333;
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
`;

const Form = styled.form`
  background-color: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #6ed216;
    box-shadow: 0 0 0 2px rgba(110, 210, 22, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #6ed216;
    box-shadow: 0 0 0 2px rgba(110, 210, 22, 0.2);
  }
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 5px;
`;

const SubmitButton = styled.button`
  background-color: #6ed216;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px 20px;
  font-size: 16px;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.3s;

  &:hover {
    background-color: #5cb70f;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorContainer = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 15px;
  border-radius: 5px;
  text-align: center;
  margin-bottom: 20px;
`;

const SuccessContainer = styled.div`
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 15px;
  border-radius: 5px;
  text-align: center;
  margin-bottom: 20px;
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: #666;

  a {
    color: #6ed216;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

function RegisterPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [xandrUsername, setXandrUsername] = useState("");
  const [xandrPassword, setXandrPassword] = useState("");
  const [role, setRole] = useState("salesperson");
  const [salesPersonName, setSalesPersonName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const apiUrl =
    process.env.REACT_APP_API_URL || "https://report-campaign.sirdata.io";

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (role === "salesperson" && !salesPersonName) {
      setError("Le nom du commercial est requis pour un compte commercial");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${apiUrl}/api/auth/register`, {
        username,
        password,
        xandrUsername,
        xandrPassword,
        role,
        salesPersonName: role === "salesperson" ? salesPersonName : null,
      });

      if (response.data.success) {
        setSuccess(
          "Compte créé avec succès! Redirection vers la page de connexion..."
        );
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError("Erreur lors de l'inscription");
      }
    } catch (err) {
      console.error("Erreur d'inscription:", err);
      setError(err.response?.data?.message || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Header>
        <Title>Créer un compte</Title>
        <Subtitle>
          Inscrivez-vous pour accéder aux rapports de campagne
        </Subtitle>
      </Header>

      {error && <ErrorContainer>{error}</ErrorContainer>}
      {success && <SuccessContainer>{success}</SuccessContainer>}

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Nom d'utilisateur</Label>
          <Input
            type="text"
            placeholder="Nom d'utilisateur"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Mot de passe</Label>
          <Input
            type="password"
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Confirmer le mot de passe</Label>
          <Input
            type="password"
            placeholder="Confirmer le mot de passe"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Identifiant Xandr (optionnel)</Label>
          <Input
            type="text"
            placeholder="Identifiant Xandr"
            value={xandrUsername}
            onChange={(e) => setXandrUsername(e.target.value)}
          />
          <HelpText>
            Vous pourrez configurer ces informations ultérieurement
          </HelpText>
        </FormGroup>

        <FormGroup>
          <Label>Mot de passe Xandr (optionnel)</Label>
          <Input
            type="password"
            placeholder="Mot de passe Xandr"
            value={xandrPassword}
            onChange={(e) => setXandrPassword(e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Rôle</Label>
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="salesperson">Commercial</option>
            <option value="admin">Administrateur</option>
          </Select>
        </FormGroup>

        {role === "salesperson" && (
          <FormGroup>
            <Label>Nom du commercial (dans Xandr)</Label>
            <Input
              type="text"
              placeholder="Nom exact du commercial dans Xandr"
              value={salesPersonName}
              onChange={(e) => setSalesPersonName(e.target.value)}
              required={role === "salesperson"}
            />
            <HelpText>
              Ce nom doit correspondre exactement au champ
              "salesperson_for_advertiser" dans Xandr
            </HelpText>
          </FormGroup>
        )}

        <SubmitButton type="submit" disabled={loading}>
          {loading ? "Inscription en cours..." : "S'inscrire"}
        </SubmitButton>

        <LoginLink>
          Vous avez déjà un compte? <Link to="/login">Se connecter</Link>
        </LoginLink>
      </Form>
    </PageContainer>
  );
}

export default RegisterPage;
